import type {
  CmsPagesExportFile,
  CmsPagesImportPreview,
  CmsPagesImportResult,
} from "../../../types/cms";
import { isSectionHome } from "../pageAddress";
import { DEFAULT_PAGE_SECTION, type PageSection } from "../pageSection";
import type {
  CmsPagesImportPreviewSidebar,
  CmsPagesImportSidebar,
  CmsPagesImportSidebarMatch,
  ImportedPage,
  ImportedPages,
  PageLinksLookup,
} from "./pagesTransferTypes";

export type ImportSidebarAction = "join" | "new" | "none";

export type ImportSidebarSelection = {
  action: ImportSidebarAction;
  menuId: string;
};

export type ImportChangeCounts = {
  created: number;
  overwritten: number;
  kept: number;
};

type ImportPreviewPage = CmsPagesImportPreview["pages"][number];

export const NO_IMPORT_SIDEBAR: ImportSidebarSelection = { action: "none", menuId: "" };

export function defaultImportSidebar(
  sidebar: CmsPagesImportPreviewSidebar | null,
): ImportSidebarSelection {
  if (!sidebar) {
    return NO_IMPORT_SIDEBAR;
  }
  if (sidebar.suggestedMenuId) {
    return { action: "join", menuId: sidebar.suggestedMenuId };
  }
  return { action: "new", menuId: "" };
}

export function sidebarMatches(
  sidebar: CmsPagesImportPreviewSidebar,
): CmsPagesImportSidebarMatch[] {
  const suggestedMenuId = sidebar.suggestedMenuId;
  if (!suggestedMenuId || sidebar.matches.some(match => match.menuId === suggestedMenuId)) {
    return sidebar.matches;
  }
  return [
    {
      menuId: suggestedMenuId,
      name: sidebar.title,
      label: sidebar.suggestedMenuLabel ?? sidebar.title,
    },
    ...sidebar.matches,
  ];
}

export function toImportSidebar(selection: ImportSidebarSelection): CmsPagesImportSidebar | null {
  if (selection.action === "join") {
    return selection.menuId ? { action: "join", menuId: selection.menuId } : null;
  }
  return { action: selection.action };
}

function countLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function previewPageSection(page: ImportPreviewPage, file: CmsPagesExportFile): PageSection {
  return (
    page.section ??
    file.pages.find(filePage => filePage.slug === page.slug)?.section ??
    file.section ??
    DEFAULT_PAGE_SECTION
  );
}

export function describeSidebarChange(
  page: ImportPreviewPage,
  sidebar: CmsPagesImportPreviewSidebar | null,
  selection: ImportSidebarSelection,
): string | null {
  const from = page.currentSidebar ?? null;
  let to: string | null = null;
  if (page.joinsSidebar && sidebar) {
    if (selection.action === "new") {
      to = sidebar.title;
    } else if (selection.action === "join") {
      const match = sidebarMatches(sidebar).find(option => option.menuId === selection.menuId);
      to = match ? match.name : null;
    }
  }
  if (from && to && from !== to) {
    return `Moves from sidebar ${from} to ${to}`;
  }
  if (from && !to) {
    return `Leaves sidebar ${from}`;
  }
  if (!from && to) {
    return `Joins sidebar ${to}`;
  }
  return null;
}

export function hasBlockedPages(preview: CmsPagesImportPreview): boolean {
  return preview.pages.some(page => page.action === "blocked");
}

export function hasBlockedSectionHome(preview: CmsPagesImportPreview): boolean {
  return preview.pages.some(page => page.action === "blocked" && isSectionHome(page));
}

export function hasBlockedChildPages(preview: CmsPagesImportPreview): boolean {
  return preview.pages.some(page => page.action === "blocked" && !isSectionHome(page));
}

export function countImportChanges(
  preview: CmsPagesImportPreview,
  overwriteKeys: string[],
): ImportChangeCounts {
  const created = preview.pages.filter(page => page.action === "create").length;
  const existing = preview.pages.filter(page => page.action === "exists");
  const overwritten = existing.filter(page => overwriteKeys.includes(page.key)).length;
  return { created, overwritten, kept: existing.length - overwritten };
}

export function importSummary(preview: CmsPagesImportPreview, overwriteKeys: string[]): string {
  const { created, overwritten, kept } = countImportChanges(preview, overwriteKeys);
  return `${countLabel(created, "new page", "new pages")}, ${overwritten} will be overwritten, ${kept} kept. All imported pages arrive as drafts.`;
}

export function toImportedPages(
  result: CmsPagesImportResult,
  preview: CmsPagesImportPreview,
  pageLinks: PageLinksLookup,
  file: CmsPagesExportFile,
): ImportedPages {
  function toImportedPage(key: string): ImportedPage {
    const previewPage = preview.pages.find(page => page.key === key);
    if (previewPage) {
      return {
        section: previewPageSection(previewPage, file),
        slug: previewPage.slug,
        title: previewPage.title,
      };
    }
    return { section: DEFAULT_PAGE_SECTION, slug: key, title: key };
  }

  return {
    created: result.created.map(toImportedPage),
    overwritten: result.overwritten.map(toImportedPage),
    skipped: result.skipped.map(toImportedPage),
    pageLinks,
  };
}
